import React, { useMemo, useState } from "react";
import { Bot, X, Sparkles, FileText, CheckCircle2, Loader2, ThumbsDown } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";
import RenderMarkdown from "./RenderMarkdown";

/**
 * QuoteReviewModal
 *
 * Focused popup for the "Review quote" button on already-priced quotes.
 * Shows the auto-quoted price, Claude's confidence rating, and the
 * pricing calculations that led to it. When the AI quote is still
 * pending approval (`auto_quoted_at && !auto_quote_approved_at`), the
 * footer switches to a big green "Approve $X — apply to this quote"
 * CTA that promotes the number onto `amount` + moves the quote to draft
 * via POST /api/quotes/{id}/approve-auto-quote.
 *
 * Calculations are parsed from either `auto_quote_pending_notes` (fresh
 * proposals awaiting approval) or `notes` split on the `---INTERNAL---`
 * marker (post-approval, historical rows).
 */
export default function QuoteReviewModal({ quote, onClose, onApproved }) {
  const isPending = !!(quote?.auto_quoted_at && !quote?.auto_quote_approved_at
    && Number(quote?.auto_quoted_amount || 0) > 0);
  // Prefer pending notes when the AI proposal hasn't been approved yet;
  // otherwise fall back to the merged notes on the record.
  const rawNotesSource = isPending
    ? (quote?.auto_quote_pending_notes || "")
    : (quote?.notes || "");
  const parsed = useMemo(() => splitNotes(rawNotesSource), [rawNotesSource]);

  const amount = Number(quote?.auto_quoted_amount || quote?.amount || 0);
  const amountMax = Number(quote?.amount_max || 0);
  const confidence = Number(quote?.auto_quote_confidence || 0);
  const isEstimate = !!quote?.is_estimate && amountMax > amount;
  const hasCalc = !!parsed.internal.trim();
  const rawReply = (quote?.auto_quote_last_reply || "").trim();

  const confidenceMeta = getConfidenceMeta(confidence);

  const [approving, setApproving] = useState(false);
  const approve = async () => {
    if (!quote?.id) return;
    setApproving(true);
    try {
      await api.post(`/quotes/${quote.id}/approve-auto-quote`);
      toast.success(`Approved — $${amount.toLocaleString()} applied to ${quote.contact_name || "quote"}`);
      onApproved?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't approve — try again");
    } finally {
      setApproving(false);
    }
  };

  // Rejection flow — clicking "Reject AI quote" expands an inline form
  // asking WHY. The reason is logged into the AI's training corpus so
  // the next proposal for a similar vehicle/scope avoids the same miss.
  const [rejectingOpen, setRejectingOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [correctedAmount, setCorrectedAmount] = useState("");
  const [submittingReject, setSubmittingReject] = useState(false);

  const submitReject = async () => {
    const reason = (rejectReason || "").trim();
    if (reason.length < 3) {
      toast.error("Add a short reason so the AI can learn");
      return;
    }
    if (!quote?.id) return;
    setSubmittingReject(true);
    try {
      const payload = { reason };
      const corrected = parseFloat(String(correctedAmount).replace(/[^0-9.]/g, ""));
      if (!Number.isNaN(corrected) && corrected > 0) {
        payload.corrected_amount = corrected;
      }
      await api.post(`/quotes/${quote.id}/reject-auto-quote`, payload);
      toast.success("Rejected — the AI will use this on future quotes");
      onApproved?.(); // reuse the refresh callback — same semantics
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't record the rejection — try again");
    } finally {
      setSubmittingReject(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/70 backdrop-blur-sm p-4 py-10"
      onClick={onClose}
      data-testid="quote-review-modal"
    >
      <div
        className="w-full max-w-2xl glass-strong rounded-3xl border border-white/10 shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10 bg-violet-500/[0.06]">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-violet-500/20 border border-violet-400/30 flex items-center justify-center">
              <Bot className="w-5 h-5 text-violet-300" />
            </div>
            <div>
              <div className="text-white font-medium text-sm flex items-center gap-2">
                AI quote review
                {isPending && (
                  <span
                    data-testid="quote-review-pending-pill"
                    className="text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-300 dark:bg-amber-500/20 dark:text-amber-200 dark:border-amber-400/40"
                  >
                    Pending approval
                  </span>
                )}
              </div>
              <div className="text-slate-400 text-xs">
                {quote?.contact_name || "—"} · {quote?.vehicle || "—"}
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            data-testid="quote-review-close"
            className="w-9 h-9 rounded-full hover:bg-white/10 flex items-center justify-center text-slate-400 hover:text-white"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Price block */}
        <div className="px-6 py-6 border-b border-white/10">
          <div className="text-label mb-1">
            {isPending ? "AI-proposed price" : "Auto-quoted price"}
          </div>
          <div className="flex items-end gap-3 flex-wrap">
            <div className="font-heading text-5xl font-light tracking-tighter text-white" data-testid="quote-review-price">
              ${amount.toLocaleString()}
              {isEstimate && (
                <span className="text-3xl text-slate-500"> – ${amountMax.toLocaleString()}</span>
              )}
            </div>
            {confidence > 0 && (
              <div
                data-testid="quote-review-confidence"
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${confidenceMeta.pill}`}
              >
                <Sparkles className="w-3 h-3" />
                Confidence {confidence}/5 · {confidenceMeta.label}
              </div>
            )}
            {isEstimate && (
              <span className="pill pill-warning text-[10px] uppercase tracking-wider">Estimate</span>
            )}
          </div>
          {isPending && (
            <div className="mt-3 text-xs bg-amber-100 text-amber-900 border border-amber-300 dark:bg-amber-500/10 dark:text-amber-300/90 dark:border-amber-500/30 rounded-xl px-3 py-2">
              This price is only proposed by the AI. Nothing is applied to the quote until you hit <b>Approve &amp; apply</b> below.
            </div>
          )}
          {confidence > 0 && confidence <= 2 && (
            <div className="mt-3 text-xs bg-amber-100 text-amber-900 border border-amber-300 dark:bg-amber-500/10 dark:text-amber-300/90 dark:border-amber-500/30 rounded-xl px-3 py-2">
              Low-confidence estimate — Claude flagged unclear scope or a non-standard vehicle. Consider inviting the customer in for a shop visit before committing.
            </div>
          )}
        </div>

        {/* Calculations */}
        <div className="px-6 py-5 space-y-5 max-h-[52vh] overflow-y-auto">
          {parsed.customer.trim() && (
            <section data-testid="quote-review-customer-notes">
              <div className="text-label mb-2 flex items-center gap-1.5">
                <FileText className="w-3 h-3" /> Customer-facing summary
              </div>
              <div className="text-sm text-slate-300 leading-relaxed">
                <RenderMarkdown source={parsed.customer.trim()} />
              </div>
            </section>
          )}

          {hasCalc ? (
            <section data-testid="quote-review-calc">
              <div className="text-label mb-2 flex items-center gap-1.5 text-violet-300">
                <Bot className="w-3 h-3" /> Internal pricing calculation
              </div>
              <div className="rounded-2xl bg-black/40 border border-white/10 p-4 text-xs text-slate-200 whitespace-pre-wrap leading-relaxed font-mono">
                {parsed.internal.trim()}
              </div>
              <p className="text-[11px] text-slate-500 mt-2">
                Hours × rate + materials the AI used to arrive at ${amount.toLocaleString()}. Internal only — customers never see this.
              </p>
            </section>
          ) : rawReply ? (
            <section data-testid="quote-review-raw">
              <div className="text-label mb-2 flex items-center gap-1.5 text-slate-400">
                <Bot className="w-3 h-3" /> Full AI reply
              </div>
              <div className="rounded-2xl bg-black/40 border border-white/10 p-4 text-xs text-slate-300 whitespace-pre-wrap leading-relaxed max-h-72 overflow-y-auto">
                {rawReply}
              </div>
              <p className="text-[11px] text-slate-500 mt-2">
                No structured calculation was captured — showing the full AI reply for context.
              </p>
            </section>
          ) : (
            <div className="text-sm text-slate-400 bg-white/[0.03] border border-white/10 rounded-2xl p-4">
              No calculation was stored with this quote. Open the AI chat to regenerate a full pricing breakdown.
            </div>
          )}
        </div>

        {/* Inline rejection reason form — collapses the normal footer and
            replaces it with a focused "tell the AI why" prompt. */}
        {isPending && rejectingOpen && (
          <div
            className="px-6 py-5 border-t border-white/10 bg-rose-500/[0.04] space-y-3"
            data-testid="quote-review-reject-form"
          >
            <div className="flex items-center gap-2 text-sm text-white font-medium">
              <ThumbsDown className="w-4 h-4 text-rose-300" />
              Why was this quote off?
            </div>
            <p className="text-xs text-slate-400 -mt-1">
              Short honest feedback — "price too low, this needs a full strip
              and prep" — is what the AI reads before every future quote so
              it stops repeating the same miss on similar jobs.
            </p>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="e.g. Too low — this vehicle has a recessed fuel door and needs a 2-piece inlay, not a flat wrap. Charge closer to $900."
              rows={4}
              autoFocus
              data-testid="quote-review-reject-reason"
              className="w-full rounded-xl bg-black/30 border border-white/10 text-sm text-white placeholder-slate-500 px-3 py-2 focus:outline-none focus:border-rose-400/60"
            />
            <div className="flex items-center gap-3 flex-wrap">
              <div className="flex items-center gap-2 text-xs text-slate-400">
                <span>Correct price (optional)</span>
                <div className="relative">
                  <span className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-500 text-xs">$</span>
                  <input
                    value={correctedAmount}
                    onChange={(e) => setCorrectedAmount(e.target.value)}
                    placeholder="900"
                    inputMode="decimal"
                    data-testid="quote-review-reject-corrected"
                    className="w-28 rounded-lg bg-black/30 border border-white/10 text-sm text-white placeholder-slate-500 pl-5 pr-2 py-1.5 focus:outline-none focus:border-rose-400/60"
                  />
                </div>
              </div>
              <div className="flex-1" />
              <button
                onClick={() => { setRejectingOpen(false); setRejectReason(""); setCorrectedAmount(""); }}
                data-testid="quote-review-reject-cancel"
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white/5 hover:bg-white/10 text-slate-200 text-sm"
                disabled={submittingReject}
              >
                Cancel
              </button>
              <button
                onClick={submitReject}
                disabled={submittingReject || rejectReason.trim().length < 3}
                data-testid="quote-review-reject-submit"
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-rose-500 hover:bg-rose-400 text-white font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-rose-500/20"
              >
                {submittingReject ? <Loader2 className="w-4 h-4 animate-spin" /> : <ThumbsDown className="w-4 h-4" />}
                {submittingReject ? "Submitting…" : "Submit & teach AI"}
              </button>
            </div>
          </div>
        )}

        {/* Footer */}
        {!(isPending && rejectingOpen) && (
        <div className="px-6 py-4 border-t border-white/10 flex items-center justify-end gap-2 bg-white/[0.02] flex-wrap">
          {isPending && (
            <button
              data-testid="quote-review-reject"
              onClick={() => setRejectingOpen(true)}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-rose-500/10 hover:bg-rose-500/20 border border-rose-400/30 text-rose-200 text-sm mr-auto"
            >
              <ThumbsDown className="w-4 h-4" />
              Reject AI quote
            </button>
          )}
          {isPending && (
            <button
              data-testid="quote-review-approve"
              onClick={approve}
              disabled={approving}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-emerald-500 hover:bg-emerald-400 text-black font-semibold text-sm disabled:opacity-60 disabled:cursor-not-allowed shadow-lg shadow-emerald-500/20"
            >
              {approving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
              {approving ? "Approving…" : `Approve $${amount.toLocaleString()} & apply`}
            </button>
          )}
          <button
            onClick={onClose}
            data-testid="quote-review-done"
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white/5 hover:bg-white/10 text-slate-200 text-sm"
          >
            {isPending ? "Cancel" : "Done"}
          </button>
        </div>
        )}
      </div>
    </div>
  );
}

/**
 * Split the AI-generated notes on the `---INTERNAL---` marker. If no marker
 * is present we fall back to putting everything in "customer" so the shop
 * still sees the reasoning somewhere (older quotes pre-dating the split rule).
 */
function splitNotes(notes) {
  if (!notes) return { customer: "", internal: "" };
  const markers = [
    "---INTERNAL---", "---internal---",
    "--- Internal ---", "<!--INTERNAL-->",
    "**Internal notes**", "**Internal pricing**",
  ];
  for (const m of markers) {
    const idx = notes.indexOf(m);
    if (idx !== -1) {
      return {
        customer: notes.slice(0, idx),
        internal: notes.slice(idx + m.length),
      };
    }
  }
  return { customer: notes, internal: "" };
}

function getConfidenceMeta(n) {
  if (n >= 4) return {
    pill: "bg-emerald-100 text-emerald-800 border border-emerald-300 dark:bg-emerald-500/15 dark:text-emerald-300 dark:border-emerald-400/40",
    label: "high",
  };
  if (n === 3) return {
    pill: "bg-sky-100 text-sky-800 border border-sky-300 dark:bg-sky-500/15 dark:text-sky-300 dark:border-sky-400/40",
    label: "medium",
  };
  return {
    pill: "bg-amber-100 text-amber-800 border border-amber-300 dark:bg-amber-500/15 dark:text-amber-300 dark:border-amber-400/40",
    label: "rough",
  };
}
